import { useContext, useState, useEffect, useMemo } from "react";
import HomeUI from "../components/homeUI/HomeUI";
import { UserContext } from "../components/context/UseContext";
import { AppLayout } from "../layout/AppLayout";
import Header from "../components/homeUI/Header";
import { ScrollTabs } from "../components/ScrollTab";
import SEO from "../components/SEO/SEO";
import Carousel from "react-multi-carousel";
import clsx from "clsx";
import { useCampaignCard } from "../api/useCampaignCard";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../api/settings/profile";
import { useGetCampaignCategory } from "../api/campaigns/getCampaignCategory";

const colorPalette = [
  { bg: "#C5CDF9ED", activeColor: "#6B7DE8" },
  { bg: "#E4F4D4", activeColor: "#7CAF3C" },
  { bg: "#E7E8EF", activeColor: "#8B8EA8" },
  { bg: "#F8ECDD", activeColor: "#E5A852" },
  { bg: "#F8DDDE", activeColor: "#E67B7D" },
];

const responsive = {
  superLargeDesktop: {
    // the naming can be any, depends on you.
    breakpoint: { max: 4000, min: 3000 },
    items: 1,
    partialVisibilityGutter: 15,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
    partialVisibilityGutter: 15,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
    partialVisibilityGutter: 15,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    partialVisibilityGutter: 15,
  },
};

function Home() {
  const [current, setCurrent] = useState("");
  const { signedUp, profile, token } = useContext(UserContext);
  const navigate = useNavigate();
  const { getProfileInfo } = useProfile();
  const {
    getCampaignCards,
    data: campaignCards,
    loading: loadingCampaigns,
  } = useCampaignCard();

  const {
    getCampaignCategories,
    categories: apiCategories,
  } = useGetCampaignCategory();

  const categories = useMemo(() => {
    const allGames = {
      name: "All Games",
      value: "",
      ...colorPalette[0],
    };

    const dynamicCategories = apiCategories?.map((cat, index) => ({
      name: cat.name.charAt(0).toUpperCase() + cat.name.slice(1),
      value: cat.name.toLowerCase(),
      ...colorPalette[(index + 1) % colorPalette.length],
    }));

    return [allGames, ...dynamicCategories];
  }, [apiCategories]);

  useEffect(() => {
    getProfileInfo(token, true);
    getCampaignCards();
    getCampaignCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppLayout>
      <SEO
        title="Arena - Play 100+ Games & Win Rewards"
        description="Join Arena, the ultimate gamification platform. Play over 100 exciting games including board games, arcade, trivia, and more. Win amazing rewards and compete with millions of players worldwide."
        keywords="arena games, online games, gaming platform, win rewards, board games, arcade games, trivia, multiplayer games, gamification"
        url="/home"
      />
      <div className="relative 480:rounded-[30px] full-gradient">
        <div className="w-full">
          <Header signedUp={signedUp} profile={profile} />
          <div className="mt-4">
            <h3 className="text-white px-4 text-[26px] text-left tracking-tight mx-auto">
              Welcome to Arena.
            </h3>
            <p className="text-white text-xs px-4 font-medium -mt-1">
              Engage, play, and stand a chance to win lots of amazing prizes
            </p>
            <Carousel
              autoPlay={false}
              responsive={responsive}
              arrows={false}
              partialVisible={true}
              ssr={true}
              className="mt-8"
            >
              {loadingCampaigns
                ? Array(3)
                    .fill(0)
                    .map((_, i) => (
                      <div
                        key={i}
                        className="p-5 rounded-[10px] h-[140px] mx-4 bg-gray-300 animate-pulse"
                      />
                    ))
                : (campaignCards.length > 0 ? campaignCards : []).map(
                    (x, i) => {
                      const bgColors = ["#2BA960", "#FAFAFA", "#F8DBD8"];
                      const bgColor = bgColors[i % bgColors.length];
                      const isGreen = bgColor === "#2BA960";
                      return (
                        <div
                          key={i}
                          className={clsx(
                            "p-5 rounded-[10px] h-[140px] mr-4 flex flex-col cursor-pointer",
                          )}
                          style={{ backgroundColor: bgColor }}
                          onClick={() =>
                            navigate(`/campaign_id/${x.campaign_id}`)
                          }
                        >
                          <div className="flex items-center gap-5 justify-between flex-1">
                            <div className="flex flex-col justify-between gap-2">
                              <p
                                className={clsx(
                                  "font-semibold text-sm line-clamp-1",
                                  isGreen ? "text-white" : "text-[#000000]",
                                )}
                              >
                                {x.title || x.name}
                              </p>
                              <p
                                className={clsx(
                                  "text-xs leading-tight font-medium line-clamp-3",
                                  isGreen
                                    ? "text-white text-opacity-80"
                                    : "text-[#00000080]",
                                )}
                              >
                                {x.description}
                              </p>
                            </div>
                            <img
                              className="w-[75px] h-[75px] rounded-[12px] object-cover"
                              src={x.image_url}
                              alt="banner"
                              loading="lazy"
                            />
                          </div>
                          <button
                            className={clsx(
                              "text-[10px] text-left flex items-center gap-3 mt-2",
                              isGreen ? "text-white" : "text-[#000000]",
                            )}
                          >
                            {x.link_text || x.buttonText}
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 10 10"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g clipPath="url(#clip0_1849_5233)">
                                <path
                                  d="M9.0625 5C9.0625 2.76 7.24 0.9375 5 0.9375C2.76 0.9375 0.9375 2.76 0.9375 5C0.9375 7.24 2.76 9.0625 5 9.0625C7.24 9.0625 9.0625 7.24 9.0625 5ZM8.4375 5C8.4375 6.90219 6.90219 8.4375 5 8.4375C3.09781 8.4375 1.5625 6.90219 1.5625 5C1.5625 3.09781 3.09781 1.5625 5 1.5625C6.90219 1.5625 8.4375 3.09781 8.4375 5ZM6.53312 5L6.31844 4.775L4.44344 2.9L3.99344 3.35L5.64469 5L3.99469 6.65L4.44375 7.1L6.31875 5.225L6.53312 5Z"
                                  fill={isGreen ? "white" : "black"}
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_1849_5233">
                                  <rect
                                    width="10"
                                    height="10"
                                    fill="white"
                                    transform="translate(10) rotate(90)"
                                  />
                                </clipPath>
                              </defs>
                            </svg>
                          </button>
                        </div>
                      );
                    },
                  )}
            </Carousel>
            <ScrollTabs
              items={categories}
              getCategory={(val) => setCurrent(val)}
              selectedCategory={current}
              className={"mt-12"}
            />
          </div>
        </div>
        <div className="py-9">
          <HomeUI category={current} />
        </div>
      </div>
    </AppLayout>
  );
}

export default Home;
